import React, { useState, useEffect } from 'react';
import {
  Search, Wrench, Star, ArrowRight, Menu, X,
  ChevronDown, Hammer, Zap, Paintbrush, Home, Truck, TreePine,
  Moon, Sun, MapPin, Loader, XCircle, Briefcase, HandHelping,
  Camera, Music, Monitor, BookOpen, Heart, Scissors
} from 'lucide-react';
import Footer from './Footer';

const API_BASE = 'https://hive-backend-4gcf.onrender.com';

function Hexagon({ color, children }) {
  return (
    <div className="relative flex items-center justify-center" style={{ width: 56, height: 56 }}>
      <svg viewBox="0 0 56 56" className="absolute inset-0 w-full h-full" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.15))' }}>
        <polygon points="28,2 52,15 52,41 28,54 4,41 4,15" fill={color} />
      </svg>
      <div className="relative z-10 text-white">
        {children}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesDropdown, setServicesDropdown] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [viewMode, setViewMode] = useState('seeking');
  const [theme, setTheme] = useState('light');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (token) { setIsLoggedIn(true); setUserData(user ? JSON.parse(user) : null); }
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    if (savedTheme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    if (newTheme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUserData(null);
    window.location.href = '/';
  };

  const firstName = userData?.name?.split(' ')[0] || 'User';

  const triggerSearch = async (query) => {
    const q = query.trim();
    if (!q) return;
    setSearchLoading(true);
    setSearchError('');
    setSearchResults(null);
    setSearchQuery(q);
    try {
      const res = await fetch(`${API_BASE}/api/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q }),
      });
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setSearchResults({ services: data.services || [], jobs: data.jobs || [], query: q });
    } catch (err) {
      setSearchError('Could not connect to server. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = async (e) => { e.preventDefault(); await triggerSearch(searchQuery); };

  const handleCategoryClick = async (category) => {
    setActiveCategory(category.id);
    await triggerSearch(category.searchTerm);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setSearchError('');
    setActiveCategory(null);
  };

  const totalResults = searchResults ? searchResults.services.length + searchResults.jobs.length : 0;

  const categoryIcons = [
    { id: 'cleaning',    name: 'Cleaning',     searchTerm: 'Cleaning',         icon: <Zap className="w-5 h-5" />,        hex: '#3B82F6' },
    { id: 'repairs',     name: 'Home Repairs', searchTerm: 'Home Repairs',     icon: <Home className="w-5 h-5" />,       hex: '#8B5CF6' },
    { id: 'moving',      name: 'Moving',       searchTerm: 'Moving',           icon: <Truck className="w-5 h-5" />,      hex: '#F97316' },
    { id: 'outdoor',     name: 'Outdoor',      searchTerm: 'Outdoor Help',     icon: <TreePine className="w-5 h-5" />,   hex: '#22C55E' },
    { id: 'photography', name: 'Photography',  searchTerm: 'Photography',      icon: <Camera className="w-5 h-5" />,     hex: '#EC4899' },
    { id: 'content',     name: 'Content',      searchTerm: 'Content Creation', icon: <Monitor className="w-5 h-5" />,    hex: '#06B6D4' },
    { id: 'music',       name: 'Music',        searchTerm: 'Music',            icon: <Music className="w-5 h-5" />,      hex: '#A855F7' },
    { id: 'tutoring',    name: 'Tutoring',     searchTerm: 'Tutoring',         icon: <BookOpen className="w-5 h-5" />,   hex: '#EAB308' },
    { id: 'wellness',    name: 'Wellness',     searchTerm: 'Personal Training',icon: <Heart className="w-5 h-5" />,      hex: '#EF4444' },
    { id: 'beauty',      name: 'Beauty',       searchTerm: 'Beauty',           icon: <Scissors className="w-5 h-5" />,   hex: '#F43F5E' },
    { id: 'painting',    name: 'Painting',     searchTerm: 'Painting',         icon: <Paintbrush className="w-5 h-5" />, hex: '#14B8A6' },
    { id: 'mounting',    name: 'Mounting',     searchTerm: 'Mounting',         icon: <Hammer className="w-5 h-5" />,     hex: '#64748B' },
    { id: 'assembly',    name: 'Assembly',     searchTerm: 'Assembly',         icon: <Wrench className="w-5 h-5" />,     hex: '#6B7280' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300" style={{ overflowX: 'hidden' }}>

      {/* Honeycomb background */}
      <div className="honeycomb-bg" />

      {/* ── NAVIGATION ── */}
      <nav className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl shadow-lg border-b border-gray-200/50 dark:border-gray-700/50'
          : 'bg-white/60 dark:bg-gray-900/60 backdrop-blur-md'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">

            {/* Logo */}
            <button onClick={() => window.location.href = '/'} className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <img src="/logos/logo-color.png" alt="HIVE" className="h-9 w-auto dark:hidden"
                onError={e => { e.target.style.display='none'; }} />
              <img src="/logos/logo-white.png" alt="HIVE" className="h-9 w-auto hidden dark:block"
                onError={e => { e.target.style.display='none'; }} />
            </button>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center space-x-3">
              <button onClick={toggleTheme} className="p-2.5 bg-gray-100/80 dark:bg-gray-800/80 rounded-xl hover:bg-gray-200/80 transition-all">
                {theme === 'light' ? <Moon className="w-5 h-5 text-gray-700" /> : <Sun className="w-5 h-5 text-yellow-500" />}
              </button>

              <div className="relative">
                <button
                  onClick={() => { setServicesDropdown(!servicesDropdown); setUserDropdownOpen(false); }}
                  onBlur={() => setTimeout(() => setServicesDropdown(false), 200)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  Get Started <ChevronDown className={`w-4 h-4 transition-transform ${servicesDropdown ? 'rotate-180' : ''}`} />
                </button>
                {servicesDropdown && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden z-50">
                    <div className="p-2">
                      <button onClick={() => { if (!isLoggedIn) { window.location.href = '/auth'; return; } window.location.href = '/post-job'; }}
                        className="w-full text-left px-4 py-3 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-xl transition-all">
                        <p className="font-semibold text-gray-900 dark:text-white">I Need Help</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Looking for someone to do a task for you</p>
                      </button>
                      <button onClick={() => { if (!isLoggedIn) { window.location.href = '/auth'; return; } window.location.href = '/post-service'; }}
                        className="w-full text-left px-4 py-3 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-xl transition-all">
                        <p className="font-semibold text-gray-900 dark:text-white">I Offer a Service</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">You have a skill and want to earn money</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {isLoggedIn ? (
                <div className="relative">
                  <button
                    onClick={() => { setUserDropdownOpen(!userDropdownOpen); setServicesDropdown(false); }}
                    onBlur={() => setTimeout(() => setUserDropdownOpen(false), 200)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-white/80 dark:bg-gray-800/80 text-gray-900 dark:text-white rounded-xl font-semibold hover:shadow-lg transition-all border border-gray-200/50 dark:border-gray-700/50"
                  >
                    <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {userData?.name?.charAt(0) || 'U'}
                    </div>
                    {firstName}
                    <ChevronDown className={`w-4 h-4 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {userDropdownOpen && (
                    <div className="absolute top-full right-0 mt-2 w-48 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden z-50">
                      <div className="p-2">
                        <button onClick={() => window.location.href = '/dashboard'} className="w-full text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all font-medium text-sm">Dashboard</button>
                        <button onClick={() => window.location.href = '/profile'} className="w-full text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all font-medium text-sm">Profile Settings</button>
                        <hr className="border-gray-200 dark:border-gray-700 my-1" />
                        <button onClick={handleLogout} className="w-full text-left px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all font-semibold text-sm">Logout</button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => window.location.href = '/auth'}
                  className="px-5 py-2.5 bg-white/80 dark:bg-gray-800/80 text-gray-900 dark:text-white rounded-xl font-semibold hover:shadow-lg transition-all border border-gray-200/50 dark:border-gray-700/50">
                  Sign In
                </button>
              )}
            </div>

            {/* Mobile hamburger */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-gray-700 dark:text-gray-300 p-2">
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-t border-gray-200/50 dark:border-gray-700/50">
            <div className="px-4 pt-3 pb-4 space-y-1">
              {isLoggedIn && (
                <div className="flex items-center gap-3 px-3 py-2 mb-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {userData?.name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Signed in as</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">{userData?.name}</p>
                  </div>
                </div>
              )}
              <button onClick={toggleTheme} className="w-full flex items-center justify-between px-3 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all">
                <span className="text-sm font-semibold">{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
                {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4 text-yellow-500" />}
              </button>
              <button onClick={() => { if (!isLoggedIn) { window.location.href = '/auth'; return; } window.location.href = '/post-job'; }} className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">I Need Help</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Looking for someone to do a task</p>
              </button>
              <button onClick={() => { if (!isLoggedIn) { window.location.href = '/auth'; return; } window.location.href = '/post-service'; }} className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">I Offer a Service</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Have a skill and want to earn money</p>
              </button>
              {isLoggedIn ? (
                <>
                  <button onClick={() => window.location.href = '/dashboard'} className="w-full text-left px-3 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl">Dashboard</button>
                  <button onClick={() => window.location.href = '/profile'} className="w-full text-left px-3 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl">Profile Settings</button>
                  <button onClick={handleLogout} className="w-full text-left px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl">Logout</button>
                </>
              ) : (
                <button onClick={() => window.location.href = '/auth'} className="w-full px-3 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-semibold text-sm">Sign In</button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative pt-28 md:pt-40 pb-16 md:pb-24">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 -left-20 w-96 h-96 bg-yellow-400/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 -right-20 w-96 h-96 bg-yellow-400/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">

          {/* Hero text — fixed with padding to prevent gradient clip */}
          <div className="mb-8">
            <h1 className="font-black leading-tight pb-2" style={{ fontSize: 'clamp(2.2rem, 7vw, 4.5rem)' }}>
              <span className="block text-gray-900 dark:text-white">
                Book trusted help
              </span>
              <span className="block" style={{ color: '#F59E0B' }}>
                for any task, anywhere
              </span>
            </h1>
          </div>

          {/* View Mode Toggle */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl p-1.5 shadow-xl border border-gray-200/50 dark:border-gray-700/50">
              <button onClick={() => setViewMode('seeking')}
                className={`px-4 sm:px-6 py-2.5 rounded-xl font-semibold text-xs sm:text-sm transition-all whitespace-nowrap ${viewMode === 'seeking' ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-lg' : 'text-gray-600 dark:text-gray-400'}`}>
                Looking for a service
              </button>
              <button onClick={() => setViewMode('offering')}
                className={`px-4 sm:px-6 py-2.5 rounded-xl font-semibold text-xs sm:text-sm transition-all whitespace-nowrap ${viewMode === 'offering' ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-lg' : 'text-gray-600 dark:text-gray-400'}`}>
                Looking to provide
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-10">
            <form onSubmit={handleSearch}>
              <div className="relative flex items-center bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search services..."
                  className="flex-1 px-4 py-4 bg-transparent text-gray-800 dark:text-gray-200 placeholder-gray-400 outline-none text-sm sm:text-base"
                  style={{ minWidth: 0 }}
                />
                <button type="submit" disabled={searchLoading}
                  className="shrink-0 m-1.5 w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2.5 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-xl hover:shadow-lg transition-all flex items-center justify-center font-semibold disabled:opacity-50">
                  {searchLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </button>
              </div>
            </form>
          </div>

          {/* Hexagon Category Icons */}
          <div className="flex items-start gap-4 overflow-x-auto pb-4 px-2 scrollbar-hide" style={{ justifyContent: 'flex-start' }}>
            {categoryIcons.map((cat, i) => (
              <div key={i} onClick={() => handleCategoryClick(cat)}
                className={`flex flex-col items-center shrink-0 cursor-pointer transition-all duration-200 ${activeCategory === cat.id ? 'opacity-100 scale-110' : 'opacity-70 hover:opacity-100 hover:scale-105'}`}
                style={{ width: 64 }}>
                <Hexagon color={activeCategory === cat.id ? cat.hex : '#9CA3AF'}>
                  {searchLoading && activeCategory === cat.id ? <Loader className="w-5 h-5 animate-spin" /> : cat.icon}
                </Hexagon>
                <span className={`text-xs font-semibold mt-2 text-center leading-tight transition-colors ${activeCategory === cat.id ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                  {cat.name}
                </span>
                {activeCategory === cat.id && <div className="mt-1 w-6 h-0.5 rounded-full" style={{ background: cat.hex }} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RESULTS ── */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {searchError && (
            <div className="text-center py-8 text-red-500 dark:text-red-400">
              <p>{searchError}</p>
              <button onClick={clearSearch} className="mt-3 text-sm underline">Try again</button>
            </div>
          )}

          {searchResults && (
            <div className="animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                  {totalResults > 0
                    ? `${totalResults} result${totalResults !== 1 ? 's' : ''} for "${searchResults.query}"`
                    : `No results for "${searchResults.query}"`}
                </h2>
                <button onClick={clearSearch} className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-all">
                  <XCircle className="w-4 h-4" /> Clear
                </button>
              </div>

              {totalResults === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No matches yet</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">No one has listed this service yet. Post a request and let providers come to you.</p>
                  <button onClick={() => window.location.href = isLoggedIn ? '/post-job' : '/auth'}
                    className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all">
                    Post a Request
                  </button>
                </div>
              ) : (
                <div className="space-y-10">
                  {searchResults.services.length > 0 && (
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                        Service Providers <span className="text-base font-normal text-gray-400 ml-1">({searchResults.services.length})</span>
                      </h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {searchResults.services.map((service, i) => (
                          <div key={i} onClick={() => window.location.href = `/service/${service._id}`}
                            className="group bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 hover:border-yellow-400/50 hover:shadow-2xl hover:scale-[1.03] transition-all duration-300 shadow-lg cursor-pointer">
                            <div className="p-6">
                              <div className="flex items-start justify-between mb-4">
                                <div className="w-14 h-14 bg-gradient-to-br from-yellow-100 to-yellow-200 dark:from-yellow-900/30 dark:to-yellow-800/30 rounded-2xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                                  <Briefcase className="w-6 h-6 text-yellow-600" />
                                </div>
                                {service.averageRating > 0 && (
                                  <div className="flex items-center bg-yellow-50 dark:bg-yellow-900/20 px-2 py-1 rounded-full">
                                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 mr-1" />
                                    <span className="text-xs font-bold text-gray-900 dark:text-white">{service.averageRating}</span>
                                  </div>
                                )}
                              </div>
                              <h3 className="font-bold text-gray-900 dark:text-white mb-1">{service.title}</h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{service.category}</p>
                              {service.location && <p className="text-xs text-gray-400 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" />{service.location}</p>}
                              <div className="pt-3 border-t border-gray-200/50 dark:border-gray-700/50 flex justify-between items-center">
                                <span className="text-xs text-gray-500">{service.totalReviews || 0} reviews</span>
                                <span className="text-sm font-bold text-gray-900 dark:text-white">LKR {service.price?.toLocaleString()}/{service.priceType}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {searchResults.jobs.length > 0 && (
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                        Help Requests <span className="text-base font-normal text-gray-400 ml-1">({searchResults.jobs.length})</span>
                      </h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {searchResults.jobs.map((job, i) => (
                          <div key={i} onClick={() => window.location.href = `/job/${job._id}`}
                            className="group bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 hover:border-green-400/50 hover:shadow-2xl hover:scale-[1.03] transition-all duration-300 shadow-lg cursor-pointer">
                            <div className="p-6">
                              <div className="flex items-start justify-between mb-4">
                                <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30 rounded-2xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                                  <HandHelping className="w-6 h-6 text-green-600" />
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${job.urgency === 'asap' ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400' : job.urgency === 'urgent' ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400' : 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'}`}>
                                  {job.urgency === 'asap' ? 'ASAP' : job.urgency === 'urgent' ? 'Urgent' : 'Normal'}
                                </span>
                              </div>
                              <h3 className="font-bold text-gray-900 dark:text-white mb-1">{job.title}</h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{job.category}</p>
                              {job.location && <p className="text-xs text-gray-400 mb-3 flex items-center gap-1"><MapPin className="w-3 h-3" />{job.location}</p>}
                              <div className="pt-3 border-t border-gray-200/50 dark:border-gray-700/50 flex justify-between">
                                <span className="text-xs text-gray-500">Budget</span>
                                <span className="text-sm font-bold text-green-600 dark:text-green-400">
                                  {job.budget ? `LKR ${job.budget?.toLocaleString()}` : 'Open'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!searchResults && !searchError && (
            <div className="animate-fadeIn text-center py-12">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">Find what you need</h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-8">
                Search above or tap a category to instantly browse services and jobs across Sri Lanka.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={() => { if (!isLoggedIn) { window.location.href = '/auth'; return; } window.location.href = '/post-job'; }}
                  className="px-8 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all">
                  Post a Request
                </button>
                <button onClick={() => { if (!isLoggedIn) { window.location.href = '/auth'; return; } window.location.href = '/post-service'; }}
                  className="px-8 py-3 bg-white/80 dark:bg-gray-800/80 text-gray-900 dark:text-white rounded-xl font-semibold border border-gray-200/50 dark:border-gray-700/50 hover:shadow-lg transition-all">
                  List Your Service
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
        .honeycomb-bg {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          z-index: 0; pointer-events: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='100'%3E%3Cpath d='M28 66L0 50V16L28 0l28 16v34L28 66zm0-2.13L54 49.37V17.63L28 2.13 2 17.63v31.74L28 63.87z' fill='%23f59e0b' fill-opacity='0.06'/%3E%3C/svg%3E");
          background-size: 56px 100px;
        }
        nav, section, footer { position: relative; z-index: 1; }
      `}</style>
    </div>
  );
}